import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, PackageSearch, Loader2, CalendarDays, CheckCircle2, Truck, WashingMachine, Sparkles, Home } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AdBanner } from "@/components/AdBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";

const STEPS = [
  { key: "pendiente", label: "Pendiente", desc: "Pedido recibido", icon: CalendarDays },
  { key: "recogido", label: "Recogido", desc: "Tu ropa está con nosotros", icon: Truck },
  { key: "en_proceso", label: "En proceso", desc: "Lavando y cuidando tus prendas", icon: WashingMachine },
  { key: "listo", label: "Listo", desc: "Preparado para la entrega", icon: Sparkles },
  { key: "entregado", label: "Entregado", desc: "¡Disfruta de tu ropa limpia!", icon: Home },
];

export default function OrderTracking() {
  const [params] = useSearchParams();
  const [number, setNumber] = useState(params.get("pedido") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.get(`/orders/track/${encodeURIComponent(number.trim())}`);
      setOrder(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const currentIdx = order ? STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="tracking-page">
      <Navbar />
      <main className="pt-24 pb-12 sm:pb-20 max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-10">
          <span className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-cyan-50 text-cyan-600 mb-4">
            <PackageSearch className="h-7 w-7" />
          </span>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">Seguimiento de tu Pedido</h1>
          <p className="mt-3 text-slate-600 text-sm sm:text-base">
            Introduce tu número de pedido (lo tienes en el correo de confirmación, por ejemplo CM-260616-A1B2).
          </p>
        </div>
        <form onSubmit={search} className="flex gap-2 sm:gap-3 mb-8 sm:mb-10" data-testid="tracking-form">
          <Input
            data-testid="tracking-number-input"
            required
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="CM-______-____"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="search"
            className="h-12 bg-white text-base uppercase"
          />
          <Button type="submit" disabled={loading} data-testid="tracking-search-btn"
            className="bg-cyan-500 hover:bg-cyan-600 rounded-full px-7 h-12">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
            <span className="ml-2 hidden sm:inline">Buscar</span>
          </Button>
        </form>
        {error && (
          <p className="text-center text-red-600 bg-red-50 border border-red-200 rounded-xl p-4" data-testid="tracking-error">
            {error}
          </p>
        )}
        {order && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-9" data-testid="tracking-result">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 sm:mb-8">
              <div>
                <p className="text-sm text-slate-500">Pedido</p>
                <p className="font-heading font-bold text-xl text-slate-900" data-testid="tracking-order-number">{order.order_number}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Recogida: {order.pickup_date}</p>
                <p className="font-heading font-semibold text-cyan-600">{order.total.toFixed(2)} €</p>
              </div>
            </div>
            <ol className="space-y-0" data-testid="tracking-timeline">
              {STEPS.map((s, i) => {
                const done = i <= currentIdx;
                const current = i === currentIdx;
                return (
                  <li key={s.key} className="flex gap-4" data-testid={`tracking-step-${s.key}`}>
                    <div className="flex flex-col items-center">
                      <span className={`flex items-center justify-center h-11 w-11 rounded-full border-2 transition-colors ${
                        done ? "bg-cyan-500 border-cyan-500 text-white" : "bg-white border-slate-200 text-slate-300"}`}>
                        {done && !current ? <CheckCircle2 className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                      </span>
                      {i < STEPS.length - 1 && (
                        <span className={`w-0.5 flex-1 min-h-8 ${i < currentIdx ? "bg-cyan-500" : "bg-slate-200"}`} />
                      )}
                    </div>
                    <div className="pb-8">
                      <p className={`font-heading font-semibold ${done ? "text-slate-900" : "text-slate-400"}`}>
                        {s.label}
                        {current && (
                          <span className="ml-2 text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full align-middle">
                            Estado actual
                          </span>
                        )}
                      </p>
                      <p className={`text-sm ${done ? "text-slate-600" : "text-slate-400"}`}>{s.desc}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
            <div className="border-t border-slate-200 pt-5 mt-2">
              <p className="text-sm font-medium text-slate-700 mb-2">Servicios del pedido</p>
              <ul className="text-sm text-slate-600 space-y-1">
                {order.items.map((i) => (
                  <li key={i.service_id}>{i.name} x{i.quantity}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </main>
      <AdBanner className="pb-10" />
      <Footer />
    </div>
  );
}
